// The pure half of the signature pad (`shared/SignaturePad.jsx`): stroke bookkeeping, "is there enough ink to count", the crop and the
// export size, and the curve control points. No DOM and no canvas, so Vitest can pin every number; jsdom has no canvas and the drawing is
// covered by Playwright (`e2e/tests/595-signature.spec.js`).
//
// COORDINATES. A point is in PAD UNITS: the pad is always PAD_W x PAD_H (a 3:1 box) however wide it is on screen, so a stroke survives a
// resize or a phone rotating, and "enough ink" means the same thing on every device. The component converts a pointer position into units on
// the way in and units into pixels on the way out. Strokes are never mutated: every function returns a new list.

export const PAD_W = 600;
export const PAD_H = 200;                 // 3:1
export const LINE_W = 3.2;                // pen width, in pad units
export const MIN_INK = 40;                // total path length below which the pad counts as empty: a tap or a speck is not a signature
export const CROP_PAD = 12;               // margin kept around the strokes, in pad units
export const MIN_CROP = 24;               // the server refuses an image under 20px a side; a flat line is widened to at least this
export const MAX_EDGE = 1200;             // longest edge of the exported PNG, in pixels
export const EXPORT_SCALE = 2;            // pixels per pad unit before the MAX_EDGE cap (a full-width pad is exactly 1200px)

// --- strokes ------------------------------------------------------------------------------------------------------------------

/** Begin a new stroke at `pt` ({x, y}). */
export const startStroke = (strokes, pt) => [...strokes, [{ x: pt.x, y: pt.y }]];

/** Add `pt` to the stroke in progress. A point that has not moved (under `minStep` units) is dropped, so a held pen does not pile up
 *  duplicates. With no stroke begun it starts one rather than throwing, since a lost pointerdown must not lose the whole signature. */
export function extendStroke(strokes, pt, minStep = 0.5) {
  if (!strokes.length) return startStroke(strokes, pt);
  const last = strokes[strokes.length - 1];
  const prev = last[last.length - 1];
  if (prev && Math.hypot(pt.x - prev.x, pt.y - prev.y) < minStep) return strokes;
  return [...strokes.slice(0, -1), [...last, { x: pt.x, y: pt.y }]];
}

/** Remove the most recent stroke (the Undo button). */
export const undoStroke = (strokes) => strokes.slice(0, -1);

export const clearStrokes = () => [];

/** Convert a pointer position to pad units. `rect` is the canvas's on-screen box ({left, top, width, height}); the result is clamped to the pad. */
export function toPadPoint(clientX, clientY, rect) {
  if (!rect || !rect.width || !rect.height) return { x: 0, y: 0 };
  const x = ((clientX - rect.left) / rect.width) * PAD_W;
  const y = ((clientY - rect.top) / rect.height) * PAD_H;
  return { x: Math.min(PAD_W, Math.max(0, x)), y: Math.min(PAD_H, Math.max(0, y)) };
}

// --- ink ----------------------------------------------------------------------------------------------------------------------

/** The path length of one stroke, in pad units. */
export function strokeLength(stroke) {
  let n = 0;
  for (let i = 1; i < (stroke || []).length; i += 1) n += Math.hypot(stroke[i].x - stroke[i - 1].x, stroke[i].y - stroke[i - 1].y);
  return n;
}

/** The total path length of every stroke. */
export const inkLength = (strokes) => (strokes || []).reduce((n, s) => n + strokeLength(s), 0);

/** Whether the pad holds something that can be submitted: no strokes, or fewer than `min` units of ink in all, is empty. */
export const hasEnoughInk = (strokes, min = MIN_INK) => inkLength(strokes) >= min;

// --- crop and export size -----------------------------------------------------------------------------------------------------

/** The box the strokes occupy, or null for none. */
export function boundingBox(strokes) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const s of strokes || []) {
    for (const p of s) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  return minX === Infinity ? null : { minX, minY, maxX, maxY };
}

/** The rectangle to export, in pad units: the strokes' box plus `padding` (and half the pen width, so the line is not shaved), grown to
 *  at least `minSize` on each side, then clamped to the pad. Null when there are no strokes. */
export function cropRect(strokes, { padding = CROP_PAD, minSize = MIN_CROP, lineWidth = LINE_W, width = PAD_W, height = PAD_H } = {}) {
  const b = boundingBox(strokes);
  if (!b) return null;
  const m = padding + lineWidth / 2;
  let x0 = b.minX - m; let y0 = b.minY - m; let x1 = b.maxX + m; let y1 = b.maxY + m;
  if (x1 - x0 < minSize) { const c = (x0 + x1) / 2; x0 = c - minSize / 2; x1 = c + minSize / 2; }
  if (y1 - y0 < minSize) { const c = (y0 + y1) / 2; y0 = c - minSize / 2; y1 = c + minSize / 2; }
  x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(width, x1); y1 = Math.min(height, y1);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** The factor that brings the longest edge of a `w` x `h` image to `maxEdge` or under (never above 1: a small image is not blown up). */
export const edgeScale = (w, h, maxEdge = MAX_EDGE) => {
  const longest = Math.max(w, h);
  return longest > maxEdge ? maxEdge / longest : 1;
};

/** The exported PNG's size in pixels, and the units-to-pixels factor to draw it with, for a crop rectangle. */
export function exportLayout(rect, { unitScale = EXPORT_SCALE, maxEdge = MAX_EDGE } = {}) {
  if (!rect) return null;
  const k = unitScale * edgeScale(rect.w * unitScale, rect.h * unitScale, maxEdge);
  return { width: Math.max(1, Math.round(rect.w * k)), height: Math.max(1, Math.round(rect.h * k)), scale: k };
}

// --- smoothing ----------------------------------------------------------------------------------------------------------------

/** A stroke as drawing steps for a canvas, smoothed with quadratic curves: each point is a CONTROL point and each segment ends at the
 *  midpoint to the next, which makes the curve pass smoothly through the joins without overshooting. A single point is a dot, two points a line.
 *  -> { start, curves: [{cx, cy, x, y}], end } where `end` is the last raw point (a final straight run to it), or null for an empty stroke. */
export function smoothPath(stroke) {
  const n = (stroke || []).length;
  if (!n) return null;
  const start = stroke[0];
  if (n === 1) return { dot: true, start, curves: [], end: start };
  if (n === 2) return { start, curves: [], end: stroke[1] };
  const curves = [];
  for (let i = 1; i < n - 1; i += 1) {
    const c = stroke[i];
    const next = stroke[i + 1];
    curves.push({ cx: c.x, cy: c.y, x: (c.x + next.x) / 2, y: (c.y + next.y) / 2 });
  }
  return { start, curves, end: stroke[n - 1] };
}

// --- the typed alternative ----------------------------------------------------------------------------------------------------

export const TYPED_BASE_PX = 100;
export const SCRIPT_FONT = '"Snell Roundhand", "Segoe Script", "Brush Script MT", "Lucida Handwriting", "Apple Chancery", cursive';

/** The font size at which a name `measuredAtBase` pixels wide (measured at TYPED_BASE_PX) fits in `avail` pixels, never above `maxPx`. */
export function fitFontPx(measuredAtBase, avail, { base = TYPED_BASE_PX, maxPx = base } = {}) {
  if (!(measuredAtBase > 0) || !(avail > 0)) return Math.min(maxPx, base);
  return Math.min(maxPx, (base * avail) / measuredAtBase);
}

/** The exported size of a typed signature: the name at TYPED_BASE_PX plus a margin, shrunk so the longest edge is at most `maxEdge`. */
export function typedLayout(measuredAtBase, { base = TYPED_BASE_PX, margin = 24, maxEdge = MAX_EDGE } = {}) {
  const fontPx = fitFontPx(measuredAtBase, maxEdge - margin * 2, { base, maxPx: base });
  const textW = measuredAtBase * (fontPx / base);
  return { fontPx, width: Math.max(1, Math.ceil(textW + margin * 2)), height: Math.max(1, Math.ceil(fontPx * 1.5 + margin)) };
}

/** A typed name is a signature when it has a letter in it; blank space is not one. */
export const typedReady = (name) => /\S/.test(String(name || ''));

/** Strip a `data:image/png;base64,` prefix. The server tolerates it, but the stored/posted form is the bare base64. */
export const stripDataUrl = (url) => String(url || '').replace(/^data:[^,]*,/, '');
